/**
 * Live layout-mode signals derived from `matchMedia` (issue #108). Pure
 * logic, `matchMedia` injected rather than read from `window` directly, same
 * shape as `ui/hudPrefs.ts`'s storage injection — so the derivation and the
 * live-change behaviour are unit-testable in Node. `main.ts` owns the real
 * `window.matchMedia` and all DOM side effects (hiding buttons, resizing tap
 * targets); this module only decides the boolean and notifies subscribers.
 *
 * `createMediaSignal` is the generic factory; `createTouchOnlySignal` is its
 * first caller. Issue #109's compact-layout signal
 * (`(max-width: 700px) or (max-height: 500px)`) is a second call to
 * `createMediaSignal` with its own query and fallback — nothing here needs
 * to change to add it.
 */
import type { NavMode } from '../camera/cameraRig';

/**
 * The subset of the real DOM `MediaQueryList` this module depends on.
 * `window.matchMedia(query)` returns something structurally compatible with
 * this at runtime; tests inject a fake.
 */
export interface MediaQueryListLike {
  readonly matches: boolean;
  addEventListener(type: 'change', listener: (event: { matches: boolean }) => void): void;
  removeEventListener(type: 'change', listener: (event: { matches: boolean }) => void): void;
}

export type MatchMediaFn = (query: string) => MediaQueryListLike;

/** A live boolean derived from one media query. */
export interface LayoutSignal {
  /** Current value, read synchronously. Always up to date with the last
   * live change (or the initial query) — never a stale boot-time snapshot. */
  readonly matches: boolean;
  /** Registers a listener for live changes. Fires only on an actual change
   * event, never immediately on subscribe. Returns an unsubscribe function
   * that removes just this listener. */
  subscribe(listener: (matches: boolean) => void): () => void;
  /** Releases the underlying MediaQueryList listener and clears all
   * subscribers. Safe to call more than once. */
  dispose(): void;
}

/**
 * Creates a {@link LayoutSignal} for `query`. `fallback` is used as the
 * initial (and permanent, non-live) value whenever `matchMedia` is missing
 * or throws — a very old engine — so a boot on such an engine never crashes
 * and never leaves the signal in a blank/unusable state. A `matchMedia` call
 * that succeeds but whose returned MediaQueryList throws on
 * `addEventListener` (some older engines only support the deprecated
 * `addListener`) degrades the same way: the initial value stands, but it
 * will not update live.
 */
export function createMediaSignal(
  query: string,
  matchMedia: MatchMediaFn | undefined,
  fallback: boolean,
): LayoutSignal {
  const listeners = new Set<(matches: boolean) => void>();
  let mql: MediaQueryListLike | null = null;
  try {
    mql = matchMedia ? matchMedia(query) : null;
  } catch {
    mql = null;
  }
  let current = mql ? mql.matches : fallback;

  function handleChange(event: { matches: boolean }): void {
    current = event.matches;
    listeners.forEach((listener) => listener(current));
  }

  if (mql) {
    try {
      mql.addEventListener('change', handleChange);
    } catch {
      // Degrade to a static (non-live) signal rather than crash boot.
    }
  }

  return {
    get matches() {
      return current;
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    dispose() {
      listeners.clear();
      if (mql) {
        try {
          mql.removeEventListener('change', handleChange);
        } catch {
          // Matches the defensive addEventListener above.
        }
      }
    },
  };
}

/** The touch-only media query: a coarse pointer with no hover capability —
 * e.g. an iPhone, or an iPad with no trackpad/keyboard attached. An iPad
 * with a trackpad reports a fine pointer and does not match; that is by
 * design (see issue #108's predictable-failure-cases table), not a bug. */
export const TOUCH_ONLY_QUERY = '(pointer: coarse) and (hover: none)';

/**
 * Creates the touch-only layout signal (issue #108). Missing or throwing
 * `matchMedia` falls back to `false` — the full desktop control set — never
 * a blank mode control.
 */
export function createTouchOnlySignal(matchMedia?: MatchMediaFn): LayoutSignal {
  return createMediaSignal(TOUCH_ONLY_QUERY, matchMedia, false);
}

/** The compact-layout media query (issue #109): a comma is a media query
 * *list* — OR semantics — so a landscape phone (short height, wide-ish CSS
 * width) still qualifies via the height clause even where it fails the
 * width one. Drives the slim top row + bottom sheet layout (main.ts +
 * `ui/mobileSheet.ts`). */
export const COMPACT_QUERY = '(max-width: 700px), (max-height: 500px)';

/**
 * Creates the compact-layout signal (issue #109). Missing or throwing
 * `matchMedia` falls back to `false` — the full desktop layout — never a
 * blank or half-collapsed one.
 */
export function createCompactSignal(matchMedia?: MatchMediaFn): LayoutSignal {
  return createMediaSignal(COMPACT_QUERY, matchMedia, false);
}

/**
 * Decides whether a live touch-only transition must force the camera rig
 * out of Fly or Walk (issue #108's "never stranded in a mode with no
 * working input" failure case — Fly/Walk need pointer-lock mouse-look and
 * WASD, which don't exist on touch). Explore, Orbit and Route are left alone:
 * Explore is orbit-drag/pinch usable and is the touch default (issue #143,
 * reversing #142's forced-switch of Explore); Orbit and Route were always
 * touch-usable. This function only decides yes/no; the caller (`main.ts`)
 * applies a `true` result through the exact same code path as clicking the
 * default mode's button, so active-button state and route-panel visibility
 * stay consistent.
 */
export function requiresForcedOrbitSwitch(mode: NavMode, touchOnly: boolean): boolean {
  return touchOnly && (mode === 'fly' || mode === 'walk');
}
